/**
 * ActiveCallOverlay
 *
 * Renders the call UI as a persistent overlay outside the router.
 * Two modes:
 *   - "full"  → full-screen call interface (video grid, controls, duration)
 *   - "pip"   → floating pill at the bottom-right with duration + expand/end
 *
 * Also renders the incoming-call ringing screen with accept/decline.
 *
 * Supports both 1:1 and group calls with:
 *   - Dynamic participant grid (CallGrid)
 *   - Screen sharing layout (ScreenShareLayout)
 *   - Unified controls (CallControls) with screen share + raise hand
 *   - Calling state visual indicators
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StreamCall,
  StreamVideo,
  CallingState,
  useCallStateHooks,
  useCall,
} from "@stream-io/video-react-sdk";
import "@stream-io/video-react-sdk/dist/css/styles.css";
import { motion, AnimatePresence } from "framer-motion";
import { useActiveCall } from "@/features/call/context/ActiveCallContext";
import { useStreamChat } from "@/features/chat/context/StreamChatContext";
import { UserAvatar } from "@/features/chat/components/UserAvatar";
import { CallGrid } from "@/features/call/components/CallGrid";
import { CallControls } from "@/features/call/components/CallControls";
import { ScreenShareLayout } from "@/features/call/components/ScreenShareLayout";
import {
  LocalScreenShareBanner,
  ScreenShareIcon,
} from "@/features/call/components/ScreenShareIndicator";
import {
  useLocalScreenShare,
  usePresentingParticipant,
} from "@/features/call/hooks/useScreenShare";

// ─── Ringtone URL ───
// Vendored copy of @stream-io/video-react-sdk/assets/piano.mp3: the SDK's
// package "exports" map (>= 1.33) no longer exposes that path, so it can't be
// imported from the package anymore.
const RINGTONE_URL = new URL("../assets/ringtone.mp3", import.meta.url).href;

// ─── Hook: play a looping ringtone ───
const useRingtone = (shouldPlay: boolean) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!shouldPlay) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      return;
    }

    const audio = new Audio(RINGTONE_URL);
    audio.loop = true;
    audio.volume = 0.6;
    audioRef.current = audio;
    audio.play().catch((err) => {
      console.warn("[Ringtone] Autoplay blocked:", err.message);
    });

    return () => {
      audio.pause();
      audio.currentTime = 0;
      audioRef.current = null;
    };
  }, [shouldPlay]);
};

// ─── Remote Audio ───
const RemoteAudioRenderer = () => {
  const call = useCall();
  const { useRemoteParticipants } = useCallStateHooks();
  const remoteParticipants = useRemoteParticipants();

  return (
    <>
      {remoteParticipants.map((p) => (
        <RemoteAudioTrack key={p.sessionId} participant={p} call={call} />
      ))}
    </>
  );
};

const RemoteAudioTrack = ({
  participant,
  call,
}: {
  participant: {
    sessionId: string;
    userId: string;
    audioStream?: MediaStream | null;
  };
  call: ReturnType<typeof useCall>;
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const el = audioRef.current;
    if (!call || !el) return;
    const cleanup = call.bindAudioElement(
      el,
      participant.sessionId,
      "audioTrack",
    );
    return () => {
      cleanup?.();
    };
  }, [call, participant.sessionId]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !participant.audioStream) return;
    if (el.srcObject !== participant.audioStream) {
      el.srcObject = participant.audioStream;
    }
  }, [participant.audioStream]);

  return (
    <audio
      ref={audioRef}
      autoPlay
      playsInline
      data-user-id={participant.userId}
      data-session-id={participant.sessionId}
      style={{ display: "none" }}
    />
  );
};

// ─── Duration timer ───
const useCallDuration = () => {
  const { useCallSession } = useCallStateHooks();
  const session = useCallSession();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!session?.started_at) return;
    const startTime = new Date(session.started_at).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - startTime) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [session?.started_at]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

// ─── Calling state label map ───
// Built at render-time (not a static object) so labels re-translate on
// language change — see useTranslation() usage in FullCallUI.
const getCallingStateLabel = (
  t: (key: string) => string,
  callingState: string,
): string | undefined => {
  switch (callingState) {
    case CallingState.RINGING:
      return t("call.ringing");
    case CallingState.JOINING:
      return t("call.connecting");
    case CallingState.RECONNECTING:
      return t("call.reconnecting");
    case CallingState.LEFT:
      return t("call.call_ended");
    default:
      return undefined;
  }
};

// ─── Full-screen Call UI (inside StreamCall) ───
const FullCallUI = ({
  onLeave,
  onEndForAll,
  onMinimize,
  peerName,
  peerId,
  peerImage,
  isOutgoing,
  isGroupCall,
}: {
  onLeave: () => void;
  onEndForAll?: () => void;
  onMinimize: () => void;
  peerName: string;
  peerId: string;
  peerImage?: string;
  isOutgoing: boolean;
  isGroupCall: boolean;
}) => {
  const {
    useCallCallingState,
    useRemoteParticipants,
    useHasOngoingScreenShare,
    useIsCallRecordingInProgress,
  } = useCallStateHooks();
  const { t } = useTranslation();
  const callingState = useCallCallingState();
  const remoteParticipants = useRemoteParticipants();
  const hasScreenShare = useHasOngoingScreenShare();
  const isRecording = useIsCallRecordingInProgress();
  const duration = useCallDuration();
  const call = useCall();
  const activeCall = useActiveCall();
  const { allMicsMuted, isLockedByCreator, toggleAllMuteLock } = activeCall;

  // Raised hands tracking
  const [raisedHands, setRaisedHands] = useState<Set<string>>(new Set());
  const raiseHandTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const lowerHand = (userId: string) => {
    setRaisedHands((prev) => {
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
    const existing = raiseHandTimers.current.get(userId);
    if (existing) {
      clearTimeout(existing);
      raiseHandTimers.current.delete(userId);
    }
  };

  // Staged participant (creator-only spotlight)
  const [stagedUserId, setStagedUserId] = useState<string | null>(null);

  // Clear stage if the staged participant leaves
  useEffect(() => {
    if (!stagedUserId) return;
    const stillPresent = remoteParticipants.some((p) => p.userId === stagedUserId);
    if (!stillPresent) setStagedUserId(null);
  }, [remoteParticipants, stagedUserId]);

  useEffect(() => {
    if (!call) return;
    const unsub = call.on("custom", (event: any) => {
      if (event.custom?.type === "raise-hand") {
        const userId = event.user?.id;
        if (userId) {
          const isRaised = raiseHandTimers.current.has(userId);
          if (isRaised) {
            // Second tap — lower immediately
            lowerHand(userId);
          } else {
            // Raise hand + auto-dismiss after 30s
            setRaisedHands((prev) => new Set(prev).add(userId));
            const timer = setTimeout(() => lowerHand(userId), 30_000);
            raiseHandTimers.current.set(userId, timer);
          }
        }
      }
    });
    return () => {
      unsub();
      raiseHandTimers.current.forEach(clearTimeout);
      raiseHandTimers.current.clear();
    };
  }, [call]);

  // Only play ringtone for outgoing calls while ringing AND no one has joined yet.
  // Stop immediately when a remote participant appears (don't wait for callingState lag).
  useRingtone(
    isOutgoing &&
      callingState === CallingState.RINGING &&
      remoteParticipants.length === 0,
  );

  // Detect LEFT state
  useEffect(() => {
    if (callingState === CallingState.LEFT) {
      onLeave();
    }
  }, [callingState, onLeave]);

  // NOTE: call.ended / call.rejected / call.session_ended listeners live in
  // ActiveCallContext. The overlay should NOT duplicate them. It only needs
  // to detect the LEFT callingState (which the context triggers via endCall).

  // Track when remote participants first appear (for call-log purposes)
  useEffect(() => {
    if (remoteParticipants.length > 0) {
      (activeCall as any).markRemoteConnected?.();
    }
  }, [remoteParticipants.length, activeCall]);

  // Ringing screen
  if (callingState === CallingState.RINGING) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <UserAvatar name={peerName} id={peerId} image={peerImage} size={96} />
          <p className="text-white text-xl font-semibold mt-4">{peerName}</p>
          <p className="text-slate-400 text-sm mt-1 animate-pulse">
            {t("call.ringing")}
          </p>
          <button
            onClick={onMinimize}
            className="mt-6 px-4 py-2 rounded-full bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
          >
            {t("call.minimize")}
          </button>
        </div>
      </div>
    );
  }

  const stateLabel = getCallingStateLabel(t, callingState);

  return (
    <div className="flex-1 flex flex-col bg-slate-900 relative h-full overflow-hidden">
      {/* Calling state indicator */}
      <AnimatePresence>
        {stateLabel && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute top-4 left-1/2 -translate-x-1/2 z-30 px-4 py-2 rounded-full glass-dark text-white text-sm font-medium"
          >
            {stateLabel}
          </motion.div>
        )}
      </AnimatePresence>

      {/* "You are sharing <surface>" — persistent while the local user shares,
          so they always know which screen/window/tab is live */}
      <LocalScreenShareBanner />

      {/* REC indicator */}
      {isRecording && (
        <div className="absolute top-4 right-4 z-30 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600/80 backdrop-blur-sm">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <span className="text-white text-xs font-semibold tracking-wide">
            REC
          </span>
        </div>
      )}

      {/* Participant grid or screen share layout — flex-1 + min-h-0 lets it
          fill all available space above the controls without overflow.
          Must be a flex container so ScreenShareLayout's flex-1 child expands. */}
      <div className="flex-1 min-h-0 p-2 overflow-hidden flex flex-col">
        {hasScreenShare ? (
          <ScreenShareLayout raisedHands={raisedHands} />
        ) : (
          <CallGrid
            raisedHands={raisedHands}
            stagedUserId={stagedUserId}
            onStage={setStagedUserId}
            isCreator={!!onEndForAll}
          />
        )}
      </div>

      {/* Duration + Controls — always visible at the bottom */}
      <div className="shrink-0">
        <p className="text-slate-400 text-lg font-mono text-center mb-1">
          {duration}
        </p>
        <CallControls
          onLeave={onLeave}
          onEndForAll={onEndForAll}
          onMinimize={onMinimize}
          isGroupCall={isGroupCall}
          canScreenShare={isGroupCall ? !!onEndForAll : true}
          allMicsMuted={allMicsMuted}
          onToggleMuteLock={!!onEndForAll ? toggleAllMuteLock : undefined}
          isLockedByCreator={isLockedByCreator}
        />
      </div>
    </div>
  );
};

// ─── PiP Call UI (inside StreamCall) — minimized floating pill ───
// This component doesn't render anything visible — the pill UI is rendered
// by the parent. It only exists to keep SDK hooks and event listeners alive.
const PipCallUI = ({ onLeave }: { onLeave: () => void }) => {
  const { useCallCallingState, useRemoteParticipants } = useCallStateHooks();
  const callingState = useCallCallingState();
  const remoteParticipants = useRemoteParticipants();
  const activeCall = useActiveCall();

  // Keep ringtone playing in pip mode — stop when someone joins
  useRingtone(
    callingState === CallingState.RINGING && remoteParticipants.length === 0,
  );

  // NOTE: call event listeners live in ActiveCallContext. Overlay just tracks state.

  // Detect LEFT
  useEffect(() => {
    if (callingState === CallingState.LEFT) onLeave();
  }, [callingState, onLeave]);

  // Track when remote participants first appear (for call-log purposes)
  useEffect(() => {
    if (remoteParticipants.length > 0) {
      (activeCall as any).markRemoteConnected?.();
    }
  }, [remoteParticipants.length, activeCall]);

  return null; // The pill is rendered by the parent; this just keeps hooks alive
};

// ─── Incoming Call Ringing Screen ───
const IncomingRingScreen = ({
  peerName,
  peerId,
  peerImage,
  peerCompany,
  peerBatchNo,
  peerUserType,
  onAccept,
  onDecline,
}: {
  peerName: string;
  peerId: string;
  peerImage?: string;
  peerCompany?: string;
  peerBatchNo?: string;
  peerUserType?: string;
  onAccept: () => void;
  onDecline: () => void;
}) => {
  const { t } = useTranslation();
  // Play ringtone for the receiver
  useRingtone(true);

  const isOfficer = (peerUserType || "").toLowerCase() === "officer";
  const subInfo =
    !isOfficer && (peerCompany || peerBatchNo)
      ? [peerCompany, peerBatchNo].filter(Boolean).join(" · ")
      : null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-slate-900"
    >
      <motion.div
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <UserAvatar name={peerName} id={peerId} image={peerImage} size={96} />
      </motion.div>
      <p className="text-slate-400 text-lg mt-6 mb-2">{t("call.incoming_voice_call")}</p>
      <p className="text-white text-3xl font-bold mb-2">{peerName}</p>
      {subInfo && <p className="text-slate-300 text-sm mb-12">{subInfo}</p>}
      {!subInfo && <div className="mb-12" />}
      <div className="flex gap-6">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={onDecline}
          className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center text-white hover:bg-red-600 transition-colors"
          title={t("call.decline")}
        >
          <svg
            className="w-7 h-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.516l2.257-1.13a1 1 0 00.502-1.21L8.23 3.684A1 1 0 007.28 3H5z"
            />
          </svg>
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={onAccept}
          className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center text-white hover:bg-green-600 transition-colors"
          title={t("call.accept")}
        >
          <svg
            className="w-7 h-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
        </motion.button>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════
// Main exported overlay
// ═══════════════════════════════════════════
export const ActiveCallOverlay = () => {
  const { t } = useTranslation();
  const {
    call,
    peer,
    isOutgoing,
    isAccepted,
    viewMode,
    isGroupCall,
    isCallCreator,
    leaveCall,
    endCallForAll,
    acceptCall,
    declineCall,
    toggleViewMode,
    expandCall,
    showToast,
  } = useActiveCall();
  const { videoClient } = useStreamChat();
  const activeCall = useActiveCall();

  // Outgoing call timeout (30s) — use refs to avoid re-running on changes
  const endCallForAllRef = useRef(endCallForAll);
  endCallForAllRef.current = endCallForAll;
  const leaveCallRef = useRef(leaveCall);
  leaveCallRef.current = leaveCall;
  const activeCallRef = useRef(activeCall);
  activeCallRef.current = activeCall;

  useEffect(() => {
    if (!call || !isOutgoing) return;

    let answered = false;
    const timeoutId = setTimeout(() => {
      if (!answered) {
        (activeCallRef.current as any).sendCallLog?.(false);
        showToast(t("call.no_answer"), "info");
        endCallForAllRef.current(); // creator times out → end for all
      }
    }, 30000);

    const sub = call.state.participants$.subscribe((participants) => {
      if (participants.length > 1) {
        answered = true;
        clearTimeout(timeoutId);
        (activeCallRef.current as any).markRemoteConnected?.();
      }
    });

    return () => {
      clearTimeout(timeoutId);
      sub.unsubscribe();
    };
  }, [call, isOutgoing]); // stable deps only

  // Connection health monitor — only handle reconnecting-failed (not "left",
  // since "left" is already the result of a leave and would create a loop)
  useEffect(() => {
    if (!call) return;

    const sub = call.state.callingState$.subscribe((cs) => {
      if (cs === "reconnecting-failed") {
        console.warn("[ActiveCall] Connection lost, cleaning up");
        leaveCallRef.current(); // network failure → leave locally
      }
    });

    return () => sub.unsubscribe();
  }, [call]); // use ref to avoid dependency on leaveCall

  // No active call → render nothing
  if (!call || !peer || !videoClient) return null;

  // Incoming call not yet accepted → show ringing screen
  if (!isOutgoing && !isAccepted) {
    return (
      <IncomingRingScreen
        peerName={peer.name}
        peerId={peer.userId}
        peerImage={peer.image}
        peerCompany={peer.company}
        peerBatchNo={peer.batchNo}
        peerUserType={peer.userType}
        onAccept={acceptCall}
        onDecline={declineCall}
      />
    );
  }

  // Determine what the PiP end button does:
  // Group call creator → endCallForAll (red)
  // Group call member  → leaveCall (orange)
  // 1:1 call           → endCallForAll (red)
  const pipEndAction =
    isGroupCall && !isCallCreator ? leaveCall : endCallForAll;

  // ─── Active call (accepted) ───
  if (viewMode === "full") {
    return (
      <div className="fixed inset-0 z-[9998] bg-slate-900 flex flex-col">
        <StreamVideo client={videoClient}>
          <StreamCall call={call}>
            <RemoteAudioRenderer />
            <FullCallUI
              onLeave={isGroupCall ? leaveCall : endCallForAll}
              onEndForAll={
                isCallCreator && isGroupCall ? endCallForAll : undefined
              }
              onMinimize={toggleViewMode}
              peerName={peer.name}
              peerId={peer.userId}
              peerImage={peer.image}
              isOutgoing={isOutgoing}
              isGroupCall={isGroupCall}
            />
          </StreamCall>
        </StreamVideo>
      </div>
    );
  }

  // ─── PiP (minimized pill) ───
  return (
    <StreamVideo client={videoClient}>
      <StreamCall call={call}>
        {/* Keep audio + event listeners alive */}
        <RemoteAudioRenderer />
        <PipCallUI onLeave={pipEndAction} />
        {/* Floating pill */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-24 md:bottom-6 right-4 z-[9998] flex items-center gap-3 glass-dark rounded-full px-4 py-3 shadow-2xl"
        >
          <div className="relative">
            <UserAvatar
              name={peer.name}
              id={peer.userId}
              image={peer.image}
              size={36}
            />
            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-800" />
            <PipScreenShareDot />
          </div>
          <div className="flex flex-col min-w-0">
            {isGroupCall ? (
              <PipGroupLabel />
            ) : (
              <p className="text-white text-sm font-medium truncate max-w-[120px]">
                {peer.name}
              </p>
            )}
            <PipDuration />
            <PipScreenShareLabel />
          </div>
          <PipStopSharingButton />
          {/* Expand */}
          <button
            onClick={expandCall}
            className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            title={t("call.expand_call")}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
              />
            </svg>
          </button>
          {/* Leave/End — orange Leave for group members, red End for creator & 1:1 */}
          <button
            onClick={pipEndAction}
            className={`w-9 h-9 rounded-full flex items-center justify-center text-white transition-colors ${
              isGroupCall && !isCallCreator
                ? "bg-orange-500 hover:bg-orange-600"
                : "bg-red-500 hover:bg-red-600"
            }`}
            title={isGroupCall && !isCallCreator ? t("call.leave_call") : t("call.end_call")}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={
                  isGroupCall && !isCallCreator
                    ? "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    : "M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.516l2.257-1.13a1 1 0 00.502-1.21L8.23 3.684A1 1 0 007.28 3H5z"
                }
              />
            </svg>
          </button>
        </motion.div>
      </StreamCall>
    </StreamVideo>
  );
};

// Small duration text for the pip pill
const PipDuration = () => {
  const duration = useCallDuration();
  return <p className="text-green-400 text-xs font-mono">{duration}</p>;
};

// Small indigo dot on the PiP avatar, shown when a screen share is live.
// The tooltip names whoever is presenting, or the shared surface when it's us.
const PipScreenShareDot = () => {
  const { t } = useTranslation();
  const { useHasOngoingScreenShare } = useCallStateHooks();
  const hasScreenShare = useHasOngoingScreenShare();
  const presenter = usePresentingParticipant();
  const { isSharing, sourceLabel } = useLocalScreenShare();
  if (!hasScreenShare) return null;
  return (
    <div
      className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-indigo-400 rounded-full border-2 border-slate-800"
      title={
        isSharing
          ? t("call.you_are_sharing", { source: sourceLabel })
          : presenter
          ? t("call.is_presenting", { name: presenter.name || presenter.userId })
          : t("call.sharing_screen")
      }
    />
  );
};

// While minimized, the sharer still needs to see *what* is being shared —
// a dot alone isn't enough once the call UI is out of the way.
const PipScreenShareLabel = () => {
  const { t } = useTranslation();
  const { isSharing, sourceLabel } = useLocalScreenShare();
  if (!isSharing) return null;
  // The pill is narrow — show the surface itself and keep the full sentence
  // in the tooltip.
  return (
    <p
      className="text-indigo-300 text-[11px] truncate max-w-[140px]"
      title={t("call.you_are_sharing", { source: sourceLabel })}
    >
      {sourceLabel}
    </p>
  );
};

// One-click stop-sharing from the minimized pill
const PipStopSharingButton = () => {
  const { t } = useTranslation();
  const { isSharing, sourceLabel, stop } = useLocalScreenShare();
  if (!isSharing) return null;
  return (
    <button
      onClick={stop}
      className="w-9 h-9 rounded-full bg-indigo-500/30 flex items-center justify-center text-indigo-200 hover:bg-indigo-500/50 transition-colors"
      title={`${t("call.stop_sharing")} — ${sourceLabel}`}
    >
      <ScreenShareIcon className="w-4 h-4" />
    </button>
  );
};

// Group call label showing live participant count, replacing the single peer name
const PipGroupLabel = () => {
  const { t } = useTranslation();
  const { useParticipants } = useCallStateHooks();
  const count = useParticipants().length;
  return (
    <p className="text-white text-sm font-medium truncate max-w-[120px]">
      {t("call.group_call_pill", { count })}
    </p>
  );
};
